import { type LogsEvent, datadogLogs } from "@datadog/browser-logs";
import { type RumEvent, datadogRum } from "@datadog/browser-rum";

/**
 * PostHog のイベント取り込み（capture）エンドポイントのパス。
 * セルフホスト PostHog（posthog.medimo.ai 等）が 5xx を返すと posthog-js が
 * Fetch error を吐き、Datadog Logs に大量転送されてアラートのノイズになるため、
 * このパス宛のログは送信対象から除外する。
 */
const POSTHOG_CAPTURE_ENDPOINT_PATH = "/i/v0/e";

const isPostHogCaptureLog = (log: LogsEvent): boolean => {
  const url = log.http?.url ?? "";
  const message = typeof log.message === "string" ? log.message : "";
  return (
    url.includes(POSTHOG_CAPTURE_ENDPOINT_PATH) ||
    message.includes(POSTHOG_CAPTURE_ENDPOINT_PATH)
  );
};

/**
 * S3 presigned URL の署名クエリ（bearer 相当の認証情報）を含む URL から
 * クエリ文字列を除去する。録音再生などで presigned URL がリソース URL として
 * RUM に載る際に、署名情報が監視基盤へ送られるのを防ぐ。
 */
const SIGNED_URL_QUERY_PARAMS = new Set([
  // 署名バージョン 4
  "x-amz-signature",
  "x-amz-security-token",
  "x-amz-credential",
  // 署名バージョン 2
  "signature",
  "awsaccesskeyid",
]);

const stripSignedUrlQuery = (url: string): string => {
  const queryIndex = url.indexOf("?");
  if (queryIndex === -1) {
    return url;
  }
  // 部分一致で判定すると document_signature のような無関係なパラメータでも
  // クエリ全体を落とすため、パラメータ名の完全一致で判定する
  const params = new URLSearchParams(url.slice(queryIndex + 1));
  const hasSignedParam = [...params.keys()].some((key) =>
    SIGNED_URL_QUERY_PARAMS.has(key.toLowerCase()),
  );
  return hasSignedParam ? url.slice(0, queryIndex) : url;
};

/**
 * RUM に送られるエラーのうち、フロント可用性 SLO の集計から除外するノイズ判定。
 * discard はせず、beforeSend で context.noise=true を付与するだけ（送信は継続）なので、
 * Datadog 上では引き続き確認・見直しができる。SLO 側で -@context.noise:true として除外する。
 *
 * ※ 実害の信号は除外しない。特に "Failed to get JWT after retries" は未解決の認証 issue
 *   （セッション管理再設計を検討中）のため、意図的に残す。
 *
 * message 単位の除外:
 * - "ResizeObserver loop ...": レイアウト通知の取りこぼしで実害の無い既知ブラウザノイズ
 * - "Script error.": クロスオリジンスクリプトの不透明エラー（内容を取得できない）
 * - "No stack, consider using an instance of Error": Error 以外の値が throw / reject された際
 * - "Failed to fetch" / "Load failed": 一時的なネットワーク失敗。サーバ起因の可用性は
 *   backend の HTTP 成功率 SLO で見る。認証失敗は別メッセージ("Failed to get JWT...")なので残る
 */
const RUM_NOISE_ERROR_MESSAGE_PATTERNS = [
  "ResizeObserver loop",
  "Script error.",
  "No stack, consider using an instance of Error",
  "Failed to fetch",
  "Load failed",
  // 想定内の 409（要約が既に開始済みでの再トリガー。MEDIMO-2888 参照）。
  // フロントが uncaught で投げているだけの想定内応答なので SLO からは除外する
  // （graceful なハンドリングへの修正は別途）。
  "Summarization has already started",
];

/**
 * error.type 単位の除外（一時的・想定内・ブラウザ介入）。
 */
const RUM_NOISE_ERROR_TYPES = [
  "AbortError", // fetch abort / push 登録 / Service Worker 購読の一時失敗
  "ChunkLoadError", // デプロイ直後に古い chunk を掴んだ際の一時エラー（リロードで解消）
  "IgnoredEventCancel", // ブラウザ intervention（touchmove の cancel 抑制 等）
  "BeforeUnloadNoGesture", // ブラウザ intervention（beforeunload 抑制）
  "NotAuthorizedException", // 想定内: 認証情報不一致（ユーザー操作起因）
  "ExpiredCodeException", // 想定内: 確認コード期限切れ
  "CodeMismatchException", // 想定内: 確認コード誤り
  "TooManyRequestsException", // 想定内: レート制限
];

const isNoiseRumError = (error: {
  message?: string;
  type?: string;
}): boolean => {
  const message = error.message ?? "";
  const type = error.type ?? "";
  return (
    RUM_NOISE_ERROR_MESSAGE_PATTERNS.some((pattern) =>
      message.includes(pattern),
    ) || RUM_NOISE_ERROR_TYPES.includes(type)
  );
};

/**
 * Datadog RUMとLogsの初期化を行う
 * @param applicationId - Datadog RUMのアプリケーションID
 * @param clientToken - Datadogのクライアントトークン
 * @param env - 実行環境（production, staging, development）
 * @param apiUrl - トレース対象のAPIのベースURL
 * @param proxy - Datadogへのリクエストを中継するプロキシURL
 */
export const initDatadog = (
  applicationId: string,
  clientToken: string,
  env: string,
  apiUrl: string,
  proxy?: string,
) => {
  const dd_env = (() => {
    if (env === "production") return "prod";
    if (env === "staging") return "stg";
    if (env === "development") return "dev";
    return env;
  })();
  datadogRum.init({
    applicationId,
    clientToken,
    env: dd_env,
    site: "ap1.datadoghq.com",
    service: "medimo",
    version: "1.0.0",
    allowedTracingUrls: [(url) => url.startsWith(apiUrl)],
    sessionSampleRate: 20,
    sessionReplaySampleRate: 5,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask",
    allowFallbackToLocalStorage: true,
    // ノイズエラーは discard せず context.noise=true を付けて送る（方法B）。
    // SLO（フロントエラー率）側で -@context.noise:true として集計から除外する。
    beforeSend: (event: RumEvent) => {
      if (event.type === "error" && isNoiseRumError(event.error)) {
        event.context = { ...event.context, noise: true };
      }
      if (event.type === "resource") {
        event.resource.url = stripSignedUrlQuery(event.resource.url);
      }
      // 録音の読み込み失敗は resource ではなく error として送られる経路があり、
      // その場合は error.resource.url に presigned URL が載る
      if (event.type === "error" && event.error.resource !== undefined) {
        event.error.resource.url = stripSignedUrlQuery(
          event.error.resource.url,
        );
      }
      return true;
    },
    ...(proxy && { proxy }),
  });
  datadogLogs.init({
    clientToken,
    env,
    service: "medimo",
    version: "1.0.0",
    site: "ap1.datadoghq.com",
    forwardErrorsToLogs: true,
    forwardConsoleLogs: "all",
    // SRE-386: console 全転送を全セッションで送ると PHI 混入時の露出が大きいため、
    // マスキング整備までの暫定として送信セッションを 10% に絞る（エラー監視・SLO は RUM 側が主）。
    sessionSampleRate: 10,
    beforeSend: (log) => !isPostHogCaptureLog(log),
    ...(proxy && { proxy }),
  });
};

/**
 * ユーザー影響のあるエラーを RUM に明示報告する（@error.source:custom）。
 * console.error は @error.source:console 扱いとなり SLO（フロントエラー率）の集計対象外に
 * なるため、SLO で拾うべき実害エラーはこのヘルパ経由で addError する。
 */
export const reportError = (error: Error, context?: object) => {
  datadogRum.addError(error, context);
};

/**
 * Datadogで追跡するユーザー情報の型定義
 */
export type UserInfoToTrack = {
  sub: string;
  email: string;
  username: string;
  userId: string;
  accountRole: string;
  clinicName: string | null;
  facilityId?: string | null;
  facilityName?: string | null;
  groupId?: string | null;
  groupName?: string | null;
};

/**
 * ユーザー情報をDatadog RUMとLogsに設定する
 * @param userInfo - 追跡するユーザー情報
 */
export const setUserInfo = ({ sub, username, ...info }: UserInfoToTrack) => {
  const userAttributes = {
    id: sub,
    name: username,
    ...info,
  };
  datadogRum.setUser(userAttributes);
  datadogLogs.setUser(userAttributes);
};

/**
 * Datadog RUMとLogsからユーザー情報をクリアする
 * @param userInfo - クリアするユーザー情報（引数は使用されないが型チェックのために受け取る）
 */
export const removeUserInfo = () => {
  datadogRum.clearUser();
  datadogLogs.clearUser();
};

type ResizePanelSize = { width?: number; height?: number };
type OutputMethods = "COPY" | "QRCODE" | "SMARTPASTE" | "GMOHAYABUSA";
type TranscriptionMode = "whisper" | "amivoice" | "multilingual";

/**
 * Datadogで追跡するグローバルステートの型定義
 */
export type GlobalStateToTrack = {
  resizePanelSize?: ResizePanelSize | null;
  isInputNumberModeActivated: boolean;
  defaultMicrophone: MediaDeviceInfo | null;
  selectedMicrophoneId: string | null;
  selectedMicrophoneName: string | null;
  outputMethod: OutputMethods;
  isOutputWithTitle: boolean;
  transcriptionMode: TranscriptionMode;
  isAutomatedTransitionToStandardPlanEnabled: boolean;
  // 診察中に音声波形を表示する
  isRecordingStatusVisible: boolean;
  // 録音中のステータスをタイトルに表示する
  isRecordingStatusTitleVisible: boolean;
  isAutoCopyEnabled: boolean;
  isMfaEnabled: boolean;
};

/**
 * グローバルステートをDatadog RUMとLogsのコンテキストに設定する
 * @param context - 追跡するグローバルステート
 */
export const setGlobalState = (context: GlobalStateToTrack) => {
  datadogRum.setGlobalContextProperty("global", context);
  datadogLogs.setGlobalContextProperty("global", context);
};

/**
 * Datadog RUMとLogsからグローバルステートのコンテキストを削除する
 */
export const removeGlobalState = () => {
  datadogRum.removeGlobalContextProperty("global");
  datadogLogs.removeGlobalContextProperty("global");
};
